import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { MenuItem, Select, Typography, Avatar, IconButton, Tooltip } from '@mic3/platform-ui';
import TextField from '@material-ui/core/TextField';

import Icon from 'app/components/atoms/Icon/Icon';
import AvatarTooltip from 'app/components/atoms/Tooltip/Tooltip';
import Loader from 'app/components/atoms/Loader/Loader';
import { ThreadMetaItem, THREAD_META_TOOLTIP } from 'app/components/organisms/Chat/ThreadSummary';
import { openThreadView, setThreadsFilter, loadThreadsList, toggleThreadFollow, setThreadsSearch } from 'store/actions/chat/chatThreadActions';
import { loadAvatar } from 'store/actions/app/appActions';
import { formatThreadTime, formatThreadDateTime, formatChatTimestamp } from 'app/utils/chat/chatUtils';
import { getAttachmentUrl } from 'app/utils/attachments/attachmentsUtils';
import { bind, debounce } from 'app/utils/decorators/decoratorUtils';
import { isEmpty } from 'app/utils/utils';
import { get } from 'app/utils/lo/lo';

// Figma (4554-305164): the filter dropdown trigger + its menu are 164px wide.
const FILTER_WIDTH = 164;

const FilterRow = styled.div`
    /* Figma: 48px between the header divider and this row's divider */
    box-sizing: border-box;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0;
    border-bottom: 1px solid ${({ theme }) => theme.material.colors.background.divider};
    & .MuiSelect-root {
        box-sizing: border-box;
        width: ${FILTER_WIDTH}px;
        /* Figma: filter label is the lighter blue (secondary.main = #7B9EEA dark) with an 8px left inset */
        color: ${({ theme }) => theme.material.colors.secondary.main};
        font-size: 14px;
        padding-left: 8px;
        padding-right: 28px;
    }
    & .MuiSelect-icon {
        color: ${({ theme }) => theme.material.colors.secondary.main};
    }
`;

// Search field that replaces the dropdown when search is toggled on (RC behaviour).
const SearchField = styled(TextField)`
    flex: 1;
    & .MuiInputBase-input {
        font-size: 14px;
        color: ${({ theme }) => theme.material.colors.text.primary};
    }
`;

const ListWrapper = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 8px 0;
`;

// Chat-message-shaped thread item (Figma): avatar outside the bubble, header +
// text inside the bubble, meta row below, divider between threads.
const ThreadItem = styled.div`
    display: flex;
    gap: 8px;
    padding: 12px 0;
    &:not(:last-child) {
        border-bottom: 1px solid ${({ theme }) => theme.material.colors.background.divider};
    }
`;

const ThreadBody = styled.div`
    flex: 1;
    min-width: 0;
`;

const Bubble = styled.div`
    background: ${({ theme }) => theme.material.colors.background.paper};
    border-radius: 4px;
    padding: 8px 12px;
    cursor: pointer;
`;

const BubbleHeader = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

// Follow toggle in place of the read tick (Figma): bell-outline when following,
// bell-off-outline when not.
const FollowButton = styled(IconButton)`
    && {
        padding: 2px;
        color: ${({ theme }) => theme.material.colors.text.secondary};
    }
`;

const AvatarSmall = styled(Avatar)`
    width: 32px !important;
    height: 32px !important;
    font-size: 14px !important;
`;

// Same avatar tooltip as the thread VIEW (AliveChatItem uses this custom CSS atom) so
// list + view match. Open it to the right of the avatar — like the ThreadView override —
// so the list's overflow scroll container can't clip it.
const AvatarWrap = styled(AvatarTooltip)`
    flex-shrink: 0;
    display: inline-flex;
    & [alt]::after {
        top: 50%;
        left: 100%;
        transform: translateY(-50%);
        margin-left: 6px;
    }
`;

const HeaderName = styled(Typography)`
    && {
        font-size: 12px;
        color: ${({ theme }) => theme.material.colors.text.secondary};
        flex: 1;
        min-width: 0;
    }
`;

const CardText = styled(Typography)`
    && {
        margin-top: 4px;
        word-break: break-word;
    }
`;

const CardFooter = styled.div`
    display: flex;
    gap: 14px;
    margin-top: 6px;
`;

// Figma: filter dropdown options are 164x48.
const MENU_PROPS = { PaperProps: { style: { width: FILTER_WIDTH } } };

const FilterMenuItem = styled(MenuItem)`
    && {
        width: ${FILTER_WIDTH}px;
        min-height: 48px;
        box-sizing: border-box;
    }
`;

const FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'following', label: 'Following' },
    { value: 'unread', label: 'Unread' },
];

// Memoized card child — avoids arrow-in-render (CLAUDE.md convention)
class ThreadCardComponent extends PureComponent {
    static propTypes = {
        thread: PropTypes.object.isRequired,
        following: PropTypes.bool,
        avatar: PropTypes.object,
        onOpen: PropTypes.func.isRequired,
        onToggleFollow: PropTypes.func.isRequired,
        loadAvatar: PropTypes.func.isRequired,
    };

    componentDidMount() {
        // load the author's avatar reference (image + initials), same as AliveChatItem
        const username = get(this.props, 'thread.user.username');
        if (username && isEmpty(this.props.avatar)) {
            this.props.loadAvatar(username, 'user');
        }
    }

    @bind
    onClick() {
        this.props.onOpen(this.props.thread);
    }

    @bind
    onToggleFollow(event) {
        // don't open the thread when toggling follow
        event.stopPropagation();
        this.props.onToggleFollow(this.props.thread, !this.props.following);
    }

    render() {
        const { thread, following, avatar } = this.props;
        const { user: { name, username } = {}, createDate, text, file, tcount, replies, tlm } = thread;
        const displayName = name || username;
        const participantCount = (replies || []).length;
        const { image, id } = avatar || {};
        const imageSource = image ? getAttachmentUrl(id, 'user', image) : null;
        return (
            <ThreadItem>
                <AvatarWrap alt={displayName}>
                    <span alt={displayName}>
                        <AvatarSmall initials={displayName} src={imageSource} />
                    </span>
                </AvatarWrap>
                <ThreadBody>
                    <Bubble onClick={this.onClick}>
                        <BubbleHeader>
                            <HeaderName variant="caption" noWrap>
                                {`${displayName} · ${formatChatTimestamp(createDate)}`}
                            </HeaderName>
                            <Tooltip title={following ? 'Following' : 'Not Following'}>
                                <FollowButton
                                    onClick={this.onToggleFollow}
                                    aria-label={following ? 'Following' : 'Not Following'}
                                >
                                    <Icon name={following ? 'bell-outline' : 'bell-off-outline'} size="sm" />
                                </FollowButton>
                            </Tooltip>
                        </BubbleHeader>
                        <CardText variant="body2" noWrap>{text || (file && file.name)}</CardText>
                    </Bubble>
                    <CardFooter>
                        <ThreadMetaItem icon="thread" iconType="af" value={tcount || 0} tooltip={THREAD_META_TOOLTIP.replies} />
                        <ThreadMetaItem icon="account" value={participantCount} tooltip={THREAD_META_TOOLTIP.participants} />
                        {tlm && <ThreadMetaItem icon="clock-outline" value={formatThreadTime(tlm)} tooltip={formatThreadDateTime(tlm)} />}
                    </CardFooter>
                </ThreadBody>
            </ThreadItem>
        );
    }
}

const ThreadCard = connect(
    (state, ownProps) => ({
        avatar: get(state, 'admin.users.references.data', []).find(u => u.username === get(ownProps, 'thread.user.username')),
    }),
    { loadAvatar }
)(ThreadCardComponent);

class ThreadsList extends PureComponent {
    static propTypes = {
        rid: PropTypes.string.isRequired,
        threadsList: PropTypes.array,
        filter: PropTypes.string,
        searchText: PropTypes.string,
        isLoadingThreads: PropTypes.bool,
        currentUserId: PropTypes.string,
        openThreadView: PropTypes.func.isRequired,
        setThreadsFilter: PropTypes.func.isRequired,
        loadThreadsList: PropTypes.func.isRequired,
        toggleThreadFollow: PropTypes.func.isRequired,
        setThreadsSearch: PropTypes.func.isRequired,
    };

    state = { searchOpen: false, searchValue: '' };

    componentDidMount() {
        this.props.loadThreadsList(this.props.rid);
    }

    @bind
    onFilterChange(event) {
        this.props.setThreadsFilter(this.props.rid, event.target.value);
    }

    @bind
    onOpenThread(thread) {
        this.props.openThreadView(this.props.rid, thread);
    }

    @bind
    onToggleFollow(thread, follow) {
        this.props.toggleThreadFollow(thread.id, follow);
    }

    @bind
    openSearch() {
        this.setState({ searchOpen: true });
    }

    @bind
    closeSearch() {
        this.setState({ searchOpen: false, searchValue: '' });
        // Only reload when a query was actually applied — closing an untouched search
        // box must not fire a redundant reload (loader flash).
        if (this.props.searchText) {
            this.props.setThreadsSearch(this.props.rid, '');
        }
    }

    @bind
    @debounce(300)
    runSearch(text) {
        // A debounce that fires after the search box was closed must be ignored,
        // otherwise it searches stale text against a closed search.
        if (!this.state.searchOpen) {
            return;
        }
        this.props.setThreadsSearch(this.props.rid, text);
    }

    @bind
    onSearchChange(event) {
        const { value } = event.target;
        this.setState({ searchValue: value });
        this.runSearch(value);
    }

    render() {
        const { threadsList, filter, isLoadingThreads, currentUserId } = this.props;
        const { searchOpen, searchValue } = this.state;
        return (
            <>
                <FilterRow>
                    {searchOpen ? (
                        <>
                            <SearchField
                                value={searchValue}
                                onChange={this.onSearchChange}
                                placeholder="Search..."
                                autoFocus
                                InputProps={{ disableUnderline: true }}
                            />
                            <IconButton onClick={this.closeSearch} aria-label="close search">
                                <Icon name="close" size="md" />
                            </IconButton>
                        </>
                    ) : (
                        <>
                            <Select value={filter} onChange={this.onFilterChange} disableUnderline MenuProps={MENU_PROPS}>
                                {FILTERS.map(f => (
                                    <FilterMenuItem key={f.value} value={f.value}>{f.label}</FilterMenuItem>
                                ))}
                            </Select>
                            <IconButton onClick={this.openSearch} aria-label="search threads">
                                <Icon name="magnify" size="md" />
                            </IconButton>
                        </>
                    )}
                </FilterRow>
                <ListWrapper>
                    {isLoadingThreads && <Loader />}
                    {!isLoadingThreads && !(threadsList || []).length && <Typography variant="caption">No threads</Typography>}
                    {(threadsList || []).map(thread => (
                        <ThreadCard
                            key={thread.id}
                            thread={thread}
                            following={(thread.replies || []).includes(currentUserId)}
                            onOpen={this.onOpenThread}
                            onToggleFollow={this.onToggleFollow}
                        />
                    ))}
                </ListWrapper>
            </>
        );
    }
}

export default connect(
    state => ({
        threadsList: state.chat.thread.threadsList,
        filter: state.chat.thread.filter,
        searchText: state.chat.thread.searchText,
        isLoadingThreads: state.chat.thread.isLoadingThreads,
        currentUserId: get(state, 'user.chat.id'),
    }),
    { openThreadView, setThreadsFilter, loadThreadsList, toggleThreadFollow, setThreadsSearch }
)(ThreadsList);
